import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
} from '@mui/material';
import { Add, Delete, Edit, Visibility, Close, Print, TableChart, Lock } from '@mui/icons-material';
import SavingOverlay from './SavingOverlay';
import ConfirmModal from './modals/ConfirmModal';
import { Toast } from './Toast';
import { useToast } from '../hooks/useToast';
import {
  patientAssessmentsService,
  patientAssessmentErrorMessage,
  PatientAssessmentRow,
  AvailableFormRow,
  AssessmentFilterOption,
} from '../services/patientAssessmentsService';
import { colors, typography } from '../theme/designSystem';
import ClearFiltersButton from './ClearFiltersButton';
import { filterFieldSx, searchFieldSx, dateFieldSx, filterActionButtonSx } from '../theme/filterStyles';

/**
 * Aba Avaliações do cadastro do paciente (F2 — RN-537): aplicações reais dos formulários de
 * avaliação. Iniciar abre a página de preenchimento; rascunho retoma de onde parou (autosave
 * no servidor); concluída abre em visualização com resultados e pode ser reaberta em até 15
 * dias. Escopo 'own' (Profissional): vê e opera apenas as próprias aplicações — o servidor
 * garante; a tela apenas reflete. Exclusão só de rascunho, com confirmação, e sempre lógica.
 */
interface Props {
  patientId: string;
}

const formatIsoDate = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const timeOf = (iso: string): string => iso.slice(11, 16);

const fieldSx = {
  backgroundColor: '#fff',
  flex: '1 1 170px',
  minWidth: 150,
  '& .MuiOutlinedInput-root': { fontSize: '0.875rem', height: '40px' },
};

const Dot: React.FC = () => (
  <Typography component="span" sx={{ color: colors.textSecondary, fontSize: '0.9rem' }}>•</Typography>
);

const STATUS_CHIP: Record<string, { label: string; bg: string }> = {
  in_progress: { label: 'Em andamento', bg: '#ff9800' },
  completed: { label: 'Concluída', bg: '#28a745' },
};

const PatientAssessmentsTab: React.FC<Props> = ({ patientId }) => {
  const { toast, showToast, hideToast } = useToast();

  const [items, setItems] = useState<PatientAssessmentRow[]>([]);
  const [availableForms, setAvailableForms] = useState<AvailableFormRow[]>([]);
  const [formOptions, setFormOptions] = useState<AssessmentFilterOption[]>([]);
  const [requesterOptions, setRequesterOptions] = useState<AssessmentFilterOption[]>([]);

  // Filtros reativos (padrão das demais abas): aplicam ao mudar, sem botão de buscar
  const [formFilter, setFormFilter] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [requesterFilter, setRequesterFilter] = useState('');
  const hasFilters = !!(formFilter || search || from || to || requesterFilter);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [startOpen, setStartOpen] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState('');
  const [starting, setStarting] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<PatientAssessmentRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await patientAssessmentsService.listByPatient(patientId, {
        formId: formFilter,
        // Busca curta devolveria quase tudo: espera 3 letras, como nas demais abas
        search: search.trim().length >= 3 ? search.trim() : undefined,
        from,
        to,
        requestedBy: requesterFilter,
      });
      setItems(data.items);
      setAvailableForms(data.availableForms);
      setFormOptions(data.formOptions);
      setRequesterOptions(data.requesterOptions);
    } catch (e) {
      setError(patientAssessmentErrorMessage(e, 'Erro ao carregar as avaliações do paciente.'));
    } finally {
      setLoading(false);
    }
  }, [patientId, formFilter, search, from, to, requesterFilter]);

  useEffect(() => {
    const timer = setTimeout(() => { load(); }, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  const clearFilters = () => {
    setFormFilter('');
    setSearch('');
    setFrom('');
    setTo('');
    setRequesterFilter('');
  };

  // O preenchimento acontece em outra aba: ao voltar para esta, recarrega para refletir
  // o que foi respondido/concluído lá
  useEffect(() => {
    const onFocus = () => { load(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  // Nova aba (decisão do Hilton, 31/08): o cadastro do paciente continua aberto atrás
  const openApplication = (applicationId: string) => {
    window.open(
      `${window.location.origin}/?page=assessment-application&applicationId=${applicationId}`,
      '_blank',
    );
  };

  const handleStart = async () => {
    if (!selectedFormId) return;
    setStarting(true);
    try {
      const applicationId = await patientAssessmentsService.start(selectedFormId, patientId);
      setStartOpen(false);
      openApplication(applicationId);
    } catch (e) {
      showToast(patientAssessmentErrorMessage(e, 'Erro ao iniciar a avaliação.'), 'error');
    } finally {
      setStarting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await patientAssessmentsService.deleteDraft(deleteTarget.id);
      setDeleteTarget(null);
      showToast('Rascunho excluído.', 'success');
      await load();
    } catch (e) {
      showToast(patientAssessmentErrorMessage(e, 'Erro ao excluir o rascunho.'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="tab-content-section">
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <TextField
          size="small"
          label="Buscar"
          placeholder="Nome da avaliação"
          value={search}
          onChange={e => setSearch(e.target.value)}
          inputProps={{ maxLength: 100 }}
          InputLabelProps={{ shrink: true }}
          sx={filterFieldSx}
        />
        <TextField
          select
          size="small"
          label="Tipo de avaliação"
          value={formFilter}
          onChange={e => setFormFilter(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={filterFieldSx}
        >
          <MenuItem value="">Todas</MenuItem>
          {formOptions.map(o => (
            <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Solicitante"
          value={requesterFilter}
          onChange={e => setRequesterFilter(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={filterFieldSx}
        >
          <MenuItem value="">Todos</MenuItem>
          {requesterOptions.map(o => (
            <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          type="date"
          label="De"
          value={from}
          onChange={e => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={dateFieldSx}
        />
        <TextField
          size="small"
          type="date"
          label="Até"
          value={to}
          onChange={e => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={dateFieldSx}
        />
        <ClearFiltersButton onClick={clearFilters} disabled={!hasFilters} />
        <Tooltip title="Aplicar avaliação">
          <span>
            <IconButton
              onClick={() => { setSelectedFormId(''); setStartOpen(true); }}
              disabled={loading || availableForms.length === 0}
              sx={filterActionButtonSx}
            >
              <Add />
            </IconButton>
          </span>
        </Tooltip>
      </Box>


      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
          {loading ? 'Carregando...' : `Encontrados: ${items.length} ${items.length === 1 ? 'item' : 'itens'}`}
          {hasFilters && !loading ? ' no filtro' : ''}
        </Typography>
      </Box>

      {error && (
        <Typography sx={{ color: colors.error ?? '#dc3545', fontSize: typography.fontSize.sm, mb: 2 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: colors.primary }} />
        </Box>
      ) : items.length === 0 ? (
        <Typography sx={{ color: colors.textSecondary, fontSize: typography.fontSize.sm, py: 4, textAlign: 'center' }}>
          {hasFilters
            ? 'Nenhuma avaliação encontrada com os filtros aplicados.'
            : 'Nenhuma avaliação aplicada a este paciente.'}
          {!hasFilters && availableForms.length === 0 && ' Não há formulários de avaliação ativos cadastrados.'}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {items.map(item => {
            const chip = STATUS_CHIP[item.status] ?? STATUS_CHIP.in_progress;
            return (
              <Box
                key={item.id}
                sx={{
                  border: '1px solid #e0e0e0', borderRadius: '8px', p: 1.5,
                  display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center',
                  justifyContent: 'space-between', backgroundColor: '#fff',
                }}
              >
                <Box sx={{ minWidth: 0, flex: '1 1 260px' }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: colors.text }}>
                      {item.formTitle}
                    </Typography>
                    <Chip label={`v${item.formVersion}`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                    <Chip
                      label={chip.label}
                      size="small"
                      sx={{ height: 22, fontSize: '0.72rem', color: '#fff', backgroundColor: chip.bg }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                    {item.professionalName && (
                      <>
                        <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary }}>
                          {item.professionalName}
                        </Typography>
                        <Dot />
                      </>
                    )}
                    <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary }}>
                      Solicitada em {formatIsoDate(item.requestedAt)} {timeOf(item.requestedAt)}
                    </Typography>
                    <Dot />
                    <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary }}>
                      {item.answeredCount}/{item.questionsCount} respondidos
                    </Typography>
                    {item.completedAt && (
                      <>
                        <Dot />
                        <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary }}>
                          Concluída em {formatIsoDate(item.completedAt)}
                        </Typography>
                        <Dot />
                        <Typography sx={{ fontSize: '0.8rem', color: item.canReopen ? '#ff9800' : colors.textSecondary }}>
                          {item.canReopen && item.reopenDeadline
                            ? `Editável até ${formatIsoDate(item.reopenDeadline)}`
                            : item.reopenDeadline
                              ? `Definitiva desde ${formatIsoDate(item.reopenDeadline)}`
                              : 'Definitiva'}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Exportar para Excel (respostas e resultados)">
                    <IconButton
                      size="small"
                      onClick={() => patientAssessmentsService.exportXlsx(
                        item.id,
                        `avaliacao_${item.formTitle.replace(/\s+/g, '_')}.xlsx`,
                      )}
                      sx={{ color: colors.primary }}
                    >
                      <TableChart fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Imprimir a avaliação com os itens e respostas">
                    <IconButton
                      size="small"
                      onClick={() => window.open(
                        `${window.location.origin}/?page=assessment-print&applicationId=${item.id}`,
                        '_blank',
                      )}
                      sx={{ color: colors.primary }}
                    >
                      <Print fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {/* RN-540: sem acesso concedido o registro aparece — para a equipe saber que
                      a avaliação existe e com quem falar — mas não abre. */}
                  <Tooltip
                    title={!item.canRead
                      ? `Conteúdo restrito a ${item.professionalName || 'quem aplicou'}. Peça acesso ao administrador para abrir.`
                      : item.status === 'in_progress' ? 'Retomar preenchimento' : 'Abrir avaliação'}
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={!item.canRead}
                        onClick={() => openApplication(item.id)}
                        sx={{ color: colors.primary }}
                      >
                        {!item.canRead
                          ? <Lock fontSize="small" />
                          : item.status === 'in_progress' ? <Edit fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  {item.canDelete && (
                    <Tooltip title="Excluir rascunho">
                      <IconButton size="small" onClick={() => setDeleteTarget(item)} sx={{ color: '#dc3545' }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <Dialog
        open={startOpen}
        onClose={() => !starting && setStartOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ backgroundColor: colors.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1.25rem' }}>
          <Typography variant="h3" sx={{ color: '#fff', fontSize: '1.1rem', fontWeight: 600 }}>
            Aplicar avaliação
          </Typography>
          <IconButton onClick={() => setStartOpen(false)} disabled={starting} sx={{ color: '#fff' }} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: '1.25rem !important', paddingTop: '1.5rem !important' }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Formulário de avaliação"
            value={selectedFormId}
            onChange={e => setSelectedFormId(e.target.value)}
          >
            {availableForms.map(f => (
              <MenuItem key={f.id} value={f.id}>
                {f.title} (v{f.version} — {f.questionsCount} item{f.questionsCount === 1 ? '' : 's'})
              </MenuItem>
            ))}
          </TextField>
          <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textSecondary, mt: 1.5 }}>
            A aplicação começa como rascunho e cada resposta é salva automaticamente.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '1rem 1.25rem' }}>
          <Button
            variant="outlined"
            onClick={() => setStartOpen(false)}
            disabled={starting}
            sx={{ textTransform: 'none', color: '#6c757d', borderColor: '#6c757d' }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleStart}
            disabled={starting || !selectedFormId}
            sx={{ textTransform: 'none', backgroundColor: colors.primary, '&:hover': { backgroundColor: '#029AAB' } }}
          >
            Iniciar preenchimento
          </Button>
        </DialogActions>
        <SavingOverlay open={starting} message="Iniciando avaliação..." />
      </Dialog>

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title="Excluir rascunho"
        message={`Excluir o rascunho da avaliação "${deleteTarget?.formTitle ?? ''}"? As respostas já salvas deixarão de aparecer para todos.`}
        type="danger"
        confirmButtonText="Excluir rascunho"
        cancelButtonText="Cancelar"
        isProcessing={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <Toast isVisible={toast.isVisible} message={toast.message} type={toast.type} onClose={hideToast} />
    </div>
  );
};

export default PatientAssessmentsTab;
